import { FORCE_2D } from './gfx.js';
// Performance HUD + counters. Toggle: ?perf, F3, or Menu → Saves & settings → "Show performance".
// Hidden, it costs one boolean check per hook (perf.on); counters (rebuilds) are plain increments.
// Hooks: main.js times sim / DOM / view render; renderers register GL stats getters with perf.gl();
// quality guards log their steps with perf.event(); cached layers / rebuilds use perf.count().

const N = 300; // frame ring (≈5 s at 60 fps)
const ring = () => ({ a: new Float32Array(N), i: 0, n: 0 });
const push = (r, v) => { r.a[r.i] = v; r.i = (r.i + 1) % N; if (r.n < N) r.n++; };
const R = { dt: ring(), sim: ring(), dom: ring(), view: ring(), js: ring() };
const gls = new Map(); // name -> () => stats | null (null = not active)
const counters = {};   // name -> { n, ms }
const events = [];     // { t, src, msg }
let lastT = 0, cur = { sim: 0, dom: 0, view: 0 }, simTicks = 0, simMs = 0, simBus = 0, simT0 = 0;
const tickLog = []; // last 5 s of tick loops { t, n, pure, bus }
const resSubs = new Set();
const probes = new Map(); let probeOut = null, probing = false;
let resCache = null;
let el = null, timer = 0, lt = [], ltObs = null, ctxRef = null;

export const perf = {
  on: false,
    now: () => performance.now(),
  // ---- per-frame accounting (main.js) ----
  frameStart(t) {
    if (lastT) push(R.dt, t - lastT);
    lastT = t;
  },
  frameEnd(jsMs) {
    push(R.sim, cur.sim); push(R.dom, cur.dom); push(R.view, cur.view); push(R.js, jsMs + cur.sim);
    cur.sim = cur.dom = cur.view = 0;
  },
  add(k, ms) { cur[k] += ms; },
  // sim accounting: `ms` is the whole tick loop; `busMs` the part spent in UI bus handlers (toasts, modals, newspaper,
  // sound) that run synchronously inside tick() — reported separately so "sim" means the simulation itself.
  ticks(n, ms, busMs = 0) {
    simTicks += n; simMs += ms - busMs; simBus += busMs; cur.sim += ms;
    const now = performance.now(); tickLog.push({ t: now, n, pure: ms - busMs, bus: busMs });
    while (tickLog.length && now - tickLog[0].t > 5000) tickLog.shift();
  },
  busMs: 0, // running total of timed() handler ms (read before/after a tick loop)
  timed(fn, name) { return (ev) => { if (!perf.on) return fn(ev); const t0 = performance.now(); try { return fn(ev); } finally { const d = performance.now() - t0; perf.busMs += d; if (name && d > 0.5) perf.count('ev.' + name, d); } }; },
  // render resolution preference: 'auto' (cap 1.5 on high-DPR screens, raised only when measured GPU time allows),
  // 'high' (device pixel ratio up to 2), 'battery' (1.0 and 30 fps 3D). Renderers subscribe with onRes().
  get res() { if (resCache == null) { try { resCache = localStorage.getItem('driller.res') || 'auto'; } catch { resCache = 'auto'; } } return resCache; },
  setRes(v) { resCache = v; try { localStorage.setItem('driller.res', v); } catch {} for (const f of resSubs) try { f(v); } catch {} perf.event('settings', 'render resolution: ' + v); },
  onRes(f) { resSubs.add(f); return () => resSubs.delete(f); },
  probing: false, // HUD GPU probe running: main.js renders at full rate
  throttled: false, // set by main.js while the view is deliberately rendered below full rate (modal / idle / battery)
  // ---- registry ----
  gl(name, getter) { gls.set(name, getter); return () => gls.delete(name); },
  probe(name, fn) { probes.set(name, fn); return () => probes.delete(name); }, // async (say) => lines | null (not visible)
  count(name, ms = 0) { const c = counters[name] ?? (counters[name] = { n: 0, ms: 0 }); c.n++; c.ms += ms; },
  event(src, msg) { events.push({ t: performance.now(), src, msg }); if (events.length > 30) events.shift(); },
  // ---- ui ----
  init(ctx) {
    ctxRef = ctx;
    let url = false, saved = false;
    try { url = new URLSearchParams(location.search).has('perf'); saved = localStorage.getItem('driller.perf') === '1'; } catch {}
    if (url || saved) perf.show(true, !url); // ?perf is for this visit only; F3 / Settings remember the choice
    window.addEventListener('keydown', (e) => { if (e.key === 'F3') { e.preventDefault(); perf.show(!perf.on); } });
  },
  show(on, persist = true) {
    on = !!on;
    if (persist) try { localStorage.setItem('driller.perf', on ? '1' : '0'); } catch {}
    if (on === perf.on) return;
    perf.on = on;
    if (on) {
      lastT = 0; for (const r of Object.values(R)) { r.i = 0; r.n = 0; } simTicks = 0; simMs = 0; simBus = 0; tickLog.length = 0; simT0 = performance.now(); lt = [];
      mountHud();
      try { ltObs = new PerformanceObserver((l) => { for (const e of l.getEntries()) { lt.push({ t: e.startTime, d: e.duration }); if (lt.length > 200) lt.shift(); } }); ltObs.observe({ type: 'longtask' }); } catch { ltObs = null; }
      timer = setInterval(update, 500); update();
    } else {
      clearInterval(timer); ltObs?.disconnect(); ltObs = null; el?.remove(); el = null;
    }
  },
  snapshot,
  report,
};

function stat(r) {
  const n = r.n; if (!n) return { avg: 0, p50: 0, p95: 0, max: 0 };
  const a = Array.from(r.a.subarray(0, n)).sort((x, y) => x - y);
  let s = 0; for (const v of a) s += v;
  return { avg: s / n, p50: a[n >> 1], p95: a[Math.min(n - 1, Math.floor(n * 0.95))], max: a[n - 1] };
}
function activeGl() {
  const out = {};
  for (const [k, f] of gls) { try { const s = f(); if (s) out[k] = s; } catch {} }
  return out;
}
function snapshot() {
  const dt = stat(R.dt), now = performance.now();
  const secs = Math.max(0.001, (now - simT0) / 1000);
  const recentLt = lt.filter((x) => now - x.t < 10000);
  const s = ctxRef?.state?.();
  return {
    fps: dt.avg ? 1000 / dt.avg : 0, frame: dt,
    js: stat(R.js), sim: stat(R.sim), dom: stat(R.dom), view: stat(R.view),
    simPerTick: simTicks ? simMs / simTicks : 0, busPerTick: simTicks ? simBus / simTicks : 0, ticksTotal: simTicks,
    ticksPerSec: tickLog.reduce((a, x) => a + x.n, 0) / 5,
    recentTick: (() => { const n = tickLog.reduce((a, x) => a + x.n, 0); return n ? { pure: tickLog.reduce((a, x) => a + x.pure, 0) / n, bus: tickLog.reduce((a, x) => a + x.bus, 0) / n, worstLoop: Math.max(...tickLog.map((x) => x.pure + x.bus)) } : null; })(),
    gl: activeGl(),
    heapMB: performance.memory ? performance.memory.usedJSHeapSize / 1048576 : null,
    longTasks10s: { n: recentLt.length, max: Math.max(0, ...recentLt.map((x) => x.d)) },
    counters: JSON.parse(JSON.stringify(counters)),
    events: events.slice(-30).map((e) => ({ s: +(e.t / 1000).toFixed(1), src: e.src, msg: e.msg })),
    route: ctxRef?.route, day: s?.day, speed: s?.speed, paused: !!s?.paused,
  };
}
const f1 = (x) => (x == null ? '—' : (+x).toFixed(1));
const f2 = (x) => (x == null ? '—' : (+x).toFixed(2));
function glLines(gl) {
  const out = [];
  for (const [k, g] of Object.entries(gl)) {
    out.push(`${k}: ${g.calls} calls · ${(g.triangles / 1000).toFixed(1)}k tris · ${g.geometries} geo · ${g.textures} tex${g.programs != null ? ' · ' + g.programs + ' prog' : ''}`);
    if (g.res) out.push(`  render ${g.res}${g.gpuMs != null ? ` · GPU ${g.gpuMs.toFixed(1)} ms/frame` : ' · GPU time n/a'}${g.fpsCap ? ` · cap ${g.fpsCap} fps` : ''}`);
    if (g.quality) out.push(`  quality: ${Object.entries(g.quality).map(([a, b]) => a + '=' + (typeof b === 'number' ? +b.toFixed(2) : b)).join(' ')}`);
  }
  return out;
}
function hudText(x) {
  return [
    `FPS ${f1(x.fps)}   frame avg ${f1(x.frame.avg)} · p50 ${f1(x.frame.p50)} · p95 ${f1(x.frame.p95)} · max ${f1(x.frame.max)} ms`,
    `JS/frame avg ${f2(x.js.avg)} · p50 ${f2(x.js.p50)} · p95 ${f1(x.js.p95)} ms`,
    `  sim ${f2(x.sim.avg)} · DOM ${f2(x.dom.avg)} (p95 ${f1(x.dom.p95)}) · view ${f2(x.view.avg)} (p95 ${f1(x.view.p95)})`,
    `sim ${f2(x.simPerTick)} ms/tick + UI events ${f2(x.busPerTick)} ms/tick (${x.ticksTotal} ticks; last 5 s ${f1(x.ticksPerSec)}/s${x.recentTick ? `, ${f2(x.recentTick.pure)} + ${f2(x.recentTick.bus)} ms, worst loop ${f1(x.recentTick.worstLoop)} ms` : ''})`,
    ...glLines(x.gl),
    `heap ${x.heapMB == null ? 'n/a' : f1(x.heapMB) + ' MB'} · long tasks (10 s) ${x.longTasks10s.n}${x.longTasks10s.n ? ' max ' + Math.round(x.longTasks10s.max) + ' ms' : ''}`,
    `view: ${x.route ?? '?'}${x.paused ? ' (paused)' : ' ×' + (x.speed ?? '?')}`,
  ].join('\n');
}
function gpuInfo() {
  try {
    const c = document.createElement('canvas'); const g = c.getContext('webgl2') || c.getContext('webgl');
    if (!g) return 'no WebGL';
    const e = g.getExtension('WEBGL_debug_renderer_info');
    const r = e ? g.getParameter(e.UNMASKED_RENDERER_WEBGL) : g.getParameter(g.RENDERER);
    const v = e ? g.getParameter(e.UNMASKED_VENDOR_WEBGL) : g.getParameter(g.VENDOR);
    g.getExtension('WEBGL_lose_context')?.loseContext();
    return `${v} · ${r} · ${g instanceof (window.WebGL2RenderingContext ?? Object) ? 'WebGL2' : 'WebGL1'}`;
  } catch (e) { return 'unknown (' + e.message + ')'; }
}
let gpuCache = null;
function report() {
  const x = snapshot();
  gpuCache ??= gpuInfo();
  const n = navigator;
  const lines = [
    '=== Landman perf report ===',
    new Date().toISOString(),
    `UA: ${n.userAgent}`,
    `GPU: ${gpuCache}`,
    `CPU threads: ${n.hardwareConcurrency ?? '?'} · device memory: ${n.deviceMemory ?? '?'} GB`,
    `screen ${screen.width}×${screen.height} · window ${innerWidth}×${innerHeight} · DPR ${devicePixelRatio} · 2D mode: ${FORCE_2D} · render resolution setting: ${perf.res}`,
    `game day ${x.day} · route ${x.route} · speed ${x.speed}${x.paused ? ' (paused)' : ''}`,
    '',
    hudText(x),
    '',
    'frame ms: ' + JSON.stringify(x.frame, (k, v) => (typeof v === 'number' ? +v.toFixed(2) : v)),
    'counters: ' + Object.entries(x.counters).map(([k, c]) => `${k}=${c.n}${c.ms ? '/' + c.ms.toFixed(0) + 'ms' : ''}`).join(' '),
    'events: ' + (x.events.map((e) => `[${e.s}s ${e.src}] ${e.msg}`).join(' | ') || 'none'),
    ...(probeOut ? ['', probeOut] : []),
  ];
  return lines.join('\n');
}
const BTN = 'font:inherit;padding:2px 8px;background:#3a2e22;color:#f4e6cc;border:1px solid #6a5a48;border-radius:4px;cursor:pointer';
function mountHud() {
  el = document.createElement('div');
  el.className = 'perf-hud';
  el.setAttribute('role', 'status');
  Object.assign(el.style, { position: 'fixed', left: '8px', bottom: '8px', zIndex: 9999, background: 'rgba(12,10,8,.82)', color: '#e8dcc6', font: '11px/1.35 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace', padding: '6px 8px', borderRadius: '6px', maxWidth: '520px', pointerEvents: 'auto', whiteSpace: 'pre', boxShadow: '0 2px 10px rgba(0,0,0,.4)' });
  el.innerHTML = `<pre style="margin:0;font:inherit;white-space:pre-wrap"></pre><div style="display:flex;gap:6px;margin-top:4px"><button type="button" data-p="copy" style="${BTN}">Copy report</button><button type="button" data-p="probe" style="${BTN}" title="Step through the 3D quality levels (~4 s each) and measure GPU time and fps for each">Probe GPU</button><button type="button" data-p="close" style="${BTN}" title="Hide (F3)">×</button><span data-p="msg" style="opacity:.75"></span></div>`;
  el.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-p]'); if (!b) return;
    if (b.dataset.p === 'close') return perf.show(false);
    if (b.dataset.p === 'probe') {
      if (probing) return; probing = perf.probing = true; const msg = el.querySelector('[data-p=msg]');
      try {
        const out = [];
        for (const [k, f] of probes) { const rows = await f((t) => { if (msg) msg.textContent = `${k}: ${t}`; }); if (rows) out.push(`${k} quality probe (${new Date().toLocaleTimeString()}):`, ...rows.map((r) => '  ' + r)); }
        probeOut = out.length ? out.join('\n') : 'probe: open the Well view (3D) first';
        if (msg) msg.textContent = out.length ? 'Probe done — included in Copy report.' : 'Open the Well view first.';
      } finally { probing = perf.probing = false; }
      return;
    }
    if (b.dataset.p === 'copy') {
      const txt = report(); const msg = el.querySelector('[data-p=msg]');
      try { await navigator.clipboard.writeText(txt); msg.textContent = 'Copied.'; }
      catch { const ta = document.createElement('textarea'); ta.value = txt; document.body.appendChild(ta); ta.select(); let ok = false; try { ok = document.execCommand('copy'); } catch {} ta.remove(); msg.textContent = ok ? 'Copied.' : 'Copy failed — see console.'; console.log(txt); }
      setTimeout(() => { if (msg) msg.textContent = ''; }, 2500);
    }
  });
  document.body.appendChild(el);
}
function update() {
  if (!el) return;
  el.firstChild.textContent = hudText(snapshot());
}
